import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  SafetyLevel,
  TokenSortableField,
  TopTokensQuery,
  ProtectionResult,
} from 'uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks'
import { GqlResult } from 'uniswap/src/data/types'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { isTestnetChain, toGraphQLChain } from 'uniswap/src/features/chains/utils'
import { CurrencyInfo, TokenList } from 'uniswap/src/features/dataApi/types'
import { buildCurrency, buildCurrencyInfo, usePersistedError } from 'uniswap/src/features/dataApi/utils'
import { currencyId } from 'uniswap/src/utils/currencyId'
import { useMemoCompare } from 'utilities/src/react/hooks'
import { H2_NATIVE_ADDRESS } from 'uniswap/src/features/chains/chainInfo'
import { ApolloError } from '@apollo/client/errors'
import { fetchGraphqlTopTokens, TopTokensGraphqlRequest } from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'

// 将数字链 ID 转换为 GraphQL 链枚举字符串
function chainIdToGraphQLChain(chainId: number | string): string {
  const numericChainId = typeof chainId === 'string' ? parseInt(chainId) : chainId
  
  switch (numericChainId) {
    case 2582:
      return 'H2'
    case 1:
      return 'ETHEREUM'
    case 137:
      return 'POLYGON'
    case 42161:
      return 'ARBITRUM'
    case 10:
      return 'OPTIMISM'
    case 8453:
      return 'BASE'
    case 56:
      return 'BNB'
    case 43114:
      return 'AVALANCHE'
    default:
      console.warn(`Unknown chain ID: ${chainId}, defaulting to H2`)
      return 'H2'
  }
}

// 创建 H2 原生代币的 CurrencyInfo
function createH2NativeCurrencyInfo(): CurrencyInfo | null {
  const chainId = UniverseChainId.H2
  
  // 创建原生货币对象 (address 为 null 表示原生货币)
  const currency = buildCurrency({
    chainId,
    address: null, // null 表示原生货币
    decimals: 18,
    symbol: 'H2',
    name: 'H2',
  })

  if (!currency) {
    return null
  }

  return buildCurrencyInfo({
    currency,
    currencyId: currencyId(currency),
    logoUrl: undefined, // 可以后续添加 H2 logo
    safetyInfo: { tokenList: TokenList.Default, protectionResult: ProtectionResult.Benign },
    safetyLevel: SafetyLevel.Verified, // 原生代币设为已验证
    isSpam: false,
  })
}

// 将简单的 token 对象转换为 CurrencyInfo
function simpleTokenToCurrencyInfo(token: any, chainId: UniverseChainId): CurrencyInfo | null {
  if (!token) {
    return null
  }

  // 确保 token 对象包含 Apollo Client 缓存所需的字段
  // 注意：忽略接口返回的 chain 字段，强制使用项目配置的正确 chainId
  const tokenWithRequiredFields = {
    ...token,
    id: token.address, // 强制设置 id 字段为 address 值，确保 Apollo 缓存正常工作
    chain: toGraphQLChain(chainId), // 强制使用项目配置的 chainId，忽略接口返回的错误 chain 值
    address: token.address || token.id, // 确保 address 字段存在
    __typename: token.__typename || 'Token' // 确保 __typename 字段存在
  }


  const currency = buildCurrency({
    chainId,
    address: tokenWithRequiredFields.address,
    decimals: parseInt(token.decimals) || 18,
    symbol: token.symbol || 'UNKNOWN',
    name: token.name || 'Unknown Token',
  })


  if (!currency) {
    return null
  }


  const tokenCurrencyId = currencyId(currency)
  const currencyInfo = buildCurrencyInfo({
    currency,
    currencyId: tokenCurrencyId,
    logoUrl: undefined, // 简单响应不包含 logo 信息
    safetyInfo: { tokenList: TokenList.Default, protectionResult: ProtectionResult.Benign }, // 默认安全信息
    safetyLevel: SafetyLevel.StrongWarning, // 默认安全级别
    isSpam: false, // 默认不是垃圾币
    isOffical: token.isOffical, // 传递官方推荐标识
  })

  return currencyInfo
}

// 使用 REST API 获取 TopTokens
function useTopTokensApi(first: number, skip: number, skipRequest: boolean) {
  const [tokens, setTokens] = useState<any[] | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ApolloError | undefined>(undefined)
  const requestIdRef = useRef(0)

  const requestBody = useMemo((): TopTokensGraphqlRequest => {
    // 初始化查询，使用 TopTokens operationName，不带 where 条件
    const variables: TopTokensGraphqlRequest['variables'] = {
      first,
      skip,
    }

    return {
      variables,
      query: 'query TopTokens($first: Int!, $skip: Int!) { tokens(first: $first, skip: $skip, orderBy: txCount, orderDirection: desc) { id symbol name decimals txCount totalSupply __typename address chain } }',
      operationName: 'TopTokens',
    }
  }, [first, skip])

  const executeRequest = useCallback(async () => {
    if (skipRequest) {
      setLoading(false)
      return
    }

    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(undefined)

    try {
      const response = await fetchGraphqlTopTokens(requestBody)

      if (requestIdRef.current !== requestId) {
        return
      }

      setTokens(response?.data?.tokens ?? undefined)
    } catch (caughtError: unknown) {
      if (requestIdRef.current !== requestId) {
        return
      }

      setError(caughtError as ApolloError)
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [requestBody, skipRequest])

  useEffect(() => {
    executeRequest()
  }, [executeRequest])

  const refetch = useCallback(() => {
    if (skipRequest) {
      return
    }
    return executeRequest()
  }, [executeRequest, skipRequest])

  return { data: { tokens }, loading, error, refetch }
}

export function usePopularTokens(chainFilter: UniverseChainId, skip: number = 0, first: number = 10): GqlResult<CurrencyInfo[]> {
  const gqlChainFilter = toGraphQLChain(chainFilter)
  const isTestnet = isTestnetChain(chainFilter)

  // 使用 REST API 代替 GraphQL Query
  const { data, loading, error, refetch } = useTopTokensApi(first, skip, isTestnet)
  const persistedError = usePersistedError(loading, error)

  // TODO(API-482): we should be able to remove this once the backend bug is fixed.
  // There's currently a graphql backend bug where the top tokens query returns different data than the token query for some fields,
  // causing each query to override the results of the other query.
  // We partially fixed this with #12653, but there can still be other issues when the `topToken` query runs before `token`,
  // which then triggers an unnecessary re-render of the `useTopTokensQuery`.
  // Given that this hook doesn't care about `feeData` or `protectionInfo`, it's Ok to ignore those values
  // and use `useMemoCompare` with a custom comparator.
  const formattedData = useMemoCompare(
    () => {
      let tokenList: CurrencyInfo[] = []

      // 使用 REST API 数据
      if (data && data.tokens && data.tokens.length > 0) {
        tokenList = data.tokens
          .filter((token: any) => {
            // 🔧 过滤掉 WH2 代币
            const symbol = token?.symbol?.toLowerCase()
            return symbol !== 'wh2'
          })
          .map((token: any) => {
            if (!token) {
              return null
            }

            // 为 Apollo 缓存添加必需的 id 字段，并确保 chain 字段格式正确
            // 注意：强制使用项目配置的 chainId，忽略接口返回的错误 chain 值
            const tokenWithId = {
              ...token,
              id: token.address, // 强制设置 id 字段为 address 值，确保 Apollo 缓存正常工作
              chain: toGraphQLChain(chainFilter), // 强制使用项目配置的 chainId，忽略接口返回的错误 chain 值
              __typename: token.__typename || 'Token' // 确保有 __typename
            }

            // 使用专门的转换函数处理简单的 token 对象
            const currencyInfo = simpleTokenToCurrencyInfo(tokenWithId, chainFilter)
            return currencyInfo
          })
          .filter((c: any): c is CurrencyInfo => Boolean(c))
      }

      // 🎯 如果是 H2 链，在代币列表前面拼接原生代币 H2
      if (chainFilter === UniverseChainId.H2) {
        const h2NativeCurrency = createH2NativeCurrencyInfo()
        if (h2NativeCurrency) {
          // 将 H2 原生代币放在列表最前面
          tokenList = [h2NativeCurrency, ...tokenList]
        }
      }

      return tokenList.length > 0 ? tokenList : undefined
    },
    (prevData, newData) => {
      if (prevData === newData) {
        return true
      }

      if (!prevData || prevData.length !== newData?.length) {
        return false
      }

      return prevData.every((prev: any, i: number) => {
        return prev.currencyId === newData?.[i]?.currencyId
      })
    },
  )

  const result = useMemo(
    () => ({ 
      data: formattedData, 
      loading: formattedData && formattedData.length > 0 ? false : loading, // 有数据时不显示loading
      error: formattedData && formattedData.length > 0 ? undefined : persistedError, // 有数据时清除错误
      refetch 
    }),
    [formattedData, loading, persistedError, refetch],
  )
  
  
  return result
}
